import type { SpaceNestedRead } from './event';
import type { ISODateTimeString, PaginatedResponse, UUID } from './shared';
import type { PollRead } from './poll';

export type QuestionStatus = (typeof QuestionStatus)[keyof typeof QuestionStatus];

export const QuestionStatus = {
  active: 'ACTIVE',
  archived: 'ARCHIVED',
  deleted: 'DELETED',
  draft: 'DRAFT',
  answered: 'ANSWERED',
} as const;

export type QuestionVisibility = (typeof QuestionVisibility)[keyof typeof QuestionVisibility];

export const QuestionVisibility = {
  space_only: 'SPACE_ONLY',
  my_network: 'MY_NETWORK',
  space_wide: 'SPACE_WIDE',
  anyone: 'ANYONE',
} as const;

/**
 * What a post is (TECH-197). The backend adds `kind` to every question read with BE-1d; until a
 * response carries it the field is absent, which means a post. Optional here for that reason.
 */
export type QuestionKind = (typeof QuestionKind)[keyof typeof QuestionKind];

export const QuestionKind = {
  post: 'post',
  poll: 'poll',
} as const;

export type QuestionClaimStatus = (typeof QuestionClaimStatus)[keyof typeof QuestionClaimStatus];

export const QuestionClaimStatus = {
  pending: 'PENDING',
  claimed: 'CLAIMED',
  declined: 'DECLINED',
} as const;

export interface UserSummary {
  id: UUID | null;
  full_name: string | null;
  slug: string | null;
  profile_picture: string | null;
  answer: string | null;
  is_anonymous: boolean;
}

export interface EventSummary {
  id: UUID | null;
  name: string;
  slug: string | null;
  image_url: string | null;
}

export interface SpaceSummary {
  id: UUID | null;
  name: string;
  slug: string | null;
  image_url: string | null;
}

/**
 * One Space link the backend held because the member who picked the Space was still a PENDING
 * member of it (held posts, 2026-10). Listed only for the viewer the backend returns it to, the
 * member whose own join request the link waits on: everyone else gets `held_spaces: null`.
 * 'held' = waiting on a host; 'declined' = never published there, whether the request was
 * declined or withdrawn or the member was removed or blocked. An approved link becomes a live
 * Space link and leaves this list.
 */
export interface HeldSpaceSummary {
  id: UUID;
  name: string;
  slug: string | null;
  status: 'held' | 'declined';
}

export interface QuestionCreate {
  title: string;
  slug?: string | null;
  content: string;

  // None=Meetverse, [uuid]=single event, [uuid1,uuid2]=multiple events
  event_ids?: UUID[] | null;
  space_ids?: UUID[] | null;

  // Backend-populated
  user_id?: UUID | null;

  // No status and no official or featured flag: the create schema leaves all three out on
  // purpose (a post is created ACTIVE, and the badges are not an author's to set).
  is_anonymous?: boolean;
  visibility?: QuestionVisibility;

  // Topic tagging (single topic per question)
  topic_id?: UUID | null;

  // Ask-on-behalf
  asked_for_self?: boolean;
  asked_for_user_id?: UUID | null;
  asked_for_email?: string | null;
}

export interface QuestionUpdate {
  title?: string | null;
  slug?: string | null;
  content?: string | null;
  event_ids?: UUID[] | null;
  space_ids?: UUID[] | null;
  topic_id?: UUID | null;
  is_anonymous?: boolean | null;
  status?: QuestionStatus | null;
  visibility?: QuestionVisibility | null;
}

export interface QuestionRead {
  id: UUID;
  /** 'post' or 'poll' (TECH-197 BE-1d); absent on a backend without it, which means a post. */
  kind?: QuestionKind;
  title: string;
  content: string;
  slug: string | null;

  /** @deprecated Use event_summaries instead. Retained for backend serialization compatibility. */
  event_id: UUID | null;
  /** @deprecated Use event_summaries instead. Backend M2M field. */
  event_ids: UUID[] | null;
  space_id: UUID | null;
  space_ids: UUID[] | null;
  space: SpaceNestedRead | null;
  space_summaries: SpaceSummary[];
  /** Held posts: the viewer's own held Space links. On this shape only the create and edit
   * responses carry a list, for the viewer the backend returns it to ([] when nothing is held).
   * Every list and feed sends null, to that viewer too. */
  held_spaces: HeldSpaceSummary[] | null;
  /** The poll block (TECH-197 BE-1d): null for a post, absent on a backend without it. */
  poll?: PollRead | null;

  user_id: UUID | null;
  user: UserSummary | null;

  is_official: boolean;
  is_anonymous: boolean;
  is_featured: boolean;
  status: QuestionStatus;
  visibility: QuestionVisibility;

  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
  archived_at: ISODateTimeString | null;

  // Pin state (populated for questions pinned in a space)
  pinned_at: ISODateTimeString | null;
  pinned_by_user_id: UUID | null;
  pinned_by_name: string | null;

  // Live Layer v1: space-feed live boost annotation (backend QuestionRead.author_live_event)
  /** Set only by the space feed while the author is going to a live event of that space. */
  author_live_event?: EventSummary | null;

  // Async-matching UX (issue #22): the persistent matcher's state for this question, surfaced to
  // the asker. "searching" | "matched" | "no_matches" | null (never evaluated / not applicable -
  // anonymous or closed - render legacy behavior). See src/components/matching/matchStateResolver.ts.
  match_status?: string | null;
  /** True when the last matcher run's candidate pool was thin. Null while searching / never evaluated. */
  pool_is_thin?: boolean | null;

  /** @deprecated Use event_summaries[0]?.name instead. */
  event_name: string;
  /** @deprecated Use event_summaries[0]?.image_url instead. */
  event_image_url: string | null;
  event_summaries: EventSummary[];
  tag_name: string;

  topic_id: UUID | null;
  topic_name: string | null;

  i_can_help_count: number;
  me_too_count: number;
  uplifts_count: number;
  is_my_question: boolean;
  has_extra_options: boolean;

  user_has_me_too: boolean;
  user_has_i_can_help: boolean;
  user_has_bookmarked: boolean;

  // Ask-on-behalf
  asked_for_self: boolean;
  asked_for_user_id: UUID | null;
  asked_for_email: string | null;
  created_by_user_id: UUID | null;
  claim_status: QuestionClaimStatus | null;
  pre_deletion_status: QuestionStatus | null;
  asked_for_user: UserSummary | null;
  created_by_user: UserSummary | null;
}

export interface QuestionDetailRead {
  id: UUID;
  /** 'post' or 'poll' (TECH-197 BE-1d); absent on a backend without it, which means a post. */
  kind?: QuestionKind;
  slug: string | null;
  user: UserSummary | null; // null: no owner, or a hidden anonymous author (TECH-712)
  event: EventSummary;
  event_summaries: EventSummary[];
  space_id: UUID | null;
  space: SpaceNestedRead | null;
  space_summaries: SpaceSummary[];
  /** Held posts: the viewer's own held Space links. A list for the viewer the backend returns it
   * to ([] when nothing is held), null for every other viewer. */
  held_spaces: HeldSpaceSummary[] | null;
  /** The poll block (TECH-197 BE-1d): null for a post, absent on a backend without it. */
  poll?: PollRead | null;
  created_at: ISODateTimeString;

  // Pin state (populated for questions pinned in a space)
  pinned_at: ISODateTimeString | null;
  pinned_by_user_id: UUID | null;
  pinned_by_name: string | null;

  topic_id: UUID | null;
  topic_name: string | null;

  uplifts_count: number;
  me_too_count: number;
  i_can_help_count: number;
  is_my_question: boolean;
  has_extra_options: boolean;

  user_has_me_too: boolean;
  user_has_i_can_help: boolean;
  user_has_bookmarked: boolean;

  title: string;
  content: string;
  is_official: boolean;
  is_anonymous: boolean;
  is_featured: boolean;
  status: QuestionStatus;
  visibility: QuestionVisibility;

  asked_for_self: boolean;
  asked_for_user_id: UUID | null;
  asked_for_email: string | null;
  created_by_user_id: UUID | null;
  claim_status: QuestionClaimStatus | null;
  asked_for_user: UserSummary | null;
  created_by_user: UserSummary | null;

  // Async-matching UX (issue #22): mirrors QuestionRead's matcher-state fields (see there).
  match_status?: string | null;
  pool_is_thin?: boolean | null;
}

export interface QuestionProfileRead {
  id: string;
  /** 'post' or 'poll' (TECH-197 BE-1d); absent on a backend without it, which means a post. */
  kind?: QuestionKind;
  slug: string;
  title: string;
  content: string;
  created_at: ISODateTimeString;
  /** @deprecated Use event_summaries instead. */
  event_id: string | null;
  event_name: string;
  event_image_url: string | null;
  event_summaries: EventSummary[];
  space_summaries: SpaceSummary[];
  /** Held posts: the viewer's own held Space links. A list for the viewer the backend returns it
   * to ([] when nothing is held), null for every other viewer. */
  held_spaces: HeldSpaceSummary[] | null;
  /** The poll block (TECH-197 BE-1d): null for a post, absent on a backend without it. */
  poll?: PollRead | null;
  tag_name: string;

  uplifts_count: number;
  me_too_count: number;
  i_can_help_count: number;

  is_anonymous: boolean;
  is_featured: boolean;
  is_official: boolean;
  visibility: QuestionVisibility;
  is_my_question: boolean;
  has_extra_options: boolean;

  // Viewer-state flags — mirror the equivalents on QuestionRead at line ~137.
  // Backend ships these on `QuestionProfileRead` (app/schemas/question.py
  // ~line 411-416) so profile cards can render the correct selected state
  // for the viewer's prior interactions on first paint. Without these
  // threaded through, ProfileQuestionCard's optimistic hook falls back to
  // `false` and a tap on what looks like an "off" button actually toggles
  // the user's existing interaction off via the backend toggle endpoint.
  // `uplifts_count` has no flag because uplifts are repeatable, not toggleable.
  user_has_me_too: boolean;
  user_has_i_can_help: boolean;
  user_has_bookmarked: boolean;

  claim_status: QuestionClaimStatus | null;
  asked_for_user_id: string | null;
  created_by_user: UserSummary | null;
  // Beneficiary summary — lets the profile ask-on-behalf banner name the
  // supported person ("...to support Miguel"), matching the feed's QuestionRead.
  asked_for_user: UserSummary | null;
}

export interface QuestionInteractionsResponse {
  question_obj_id: UUID;
  question_slug: string;
  me_too: UserSummary[];
  i_can_help: UserSummary[];
  uplift: UserSummary[];
}

export type PaginatedQuestionsResponse = PaginatedResponse<QuestionRead>;
